import { SponsorsByEntity } from '@/models/CompanyEntity';
import { CounterpartyExposureData } from '@/models/Counterparty/Exposure';
import { Files } from '@/types/files';

import { createAction, createAsyncAction } from 'typesafe-actions';

import {
  EntityData,
  EntityDebt,
  EntityFlows,
  EntityTradeValuesData,
  EntityTransactionData,
  EntityTransactionValue,
  HedgedNotionalData,
  HistoricalEntityTransactions,
  ScenarioAnalysisData,
} from '../types';

export type GetEntityTransactionsRequestPayload = {
  company_entity_id: string;
  valuation_date: string;
  user_currency_id: number;
};
export type GetEntityTransactionsSuccessPayload = {
  data: EntityTransactionData;
};
export const getEntityTransactions = createAsyncAction(
  'entityPortfolio/getEntityTransactions/request',
  'entityPortfolio/getEntityTransactions/success',
  'entityPortfolio/getEntityTransactions/failure',
)<
  GetEntityTransactionsRequestPayload,
  GetEntityTransactionsSuccessPayload,
  undefined
>();

export type GetHistoricalEntityTransactionsRequestPayload = {
  company_entity_id: string;
  valuation_date: string;
};

export type GetHistoricalEntityTransactionsSuccessPayload = {
  data: HistoricalEntityTransactions[];
};
export const getHistoricalEntityTransactions = createAsyncAction(
  'entityPortfolio/getHistoricalEntityTransactions/request',
  'entityPortfolio/getHistoricalEntityTransactions/success',
  'entityPortfolio/getHistoricalEntityTransactions/failure',
)<
  GetHistoricalEntityTransactionsRequestPayload,
  GetHistoricalEntityTransactionsSuccessPayload,
  undefined
>();

export type GetEntityDebtRequestPayload =
  GetEntityTransactionsRequestPayload & {
    historical_date_filter?: string;
  };

export type GetEntityDebtSuccessPayload = {
  data: EntityDebt;
};
export const getEntityDebt = createAsyncAction(
  'entityPortfolio/getEntityDebt/request',
  'entityPortfolio/getEntityDebt/success',
  'entityPortfolio/getEntityDebt/failure',
)<GetEntityDebtRequestPayload, GetEntityDebtSuccessPayload, undefined>();

export type GetScenarioAnalysisDownloadSuccessPayload = {
  comp_entity_id: string;
  run_date: string;
  currency_id: number;
  ir_move: number;
  fx_move: number;
  access_fair_values: boolean;
};

export const downloadScenarioAnnalysisReport = createAsyncAction(
  'entityPortfolio/downloadScenarioAnnalysisReport/request',
  'entityPortfolio/downloadScenarioAnnalysisReport/success',
  'entityPortfolio/downloadScenarioAnnalysisReport/failure',
)<GetScenarioAnalysisDownloadSuccessPayload, undefined, undefined>();

export type GetEntityTransactionValueRequestPayload = {
  company_entity_id: string;
  run_date: string;
  user_currency_id: number;
};

export type GetEntityTransactionValueSuccessPayload = {
  data: EntityTransactionValue;
};

export const getEntityTransactionValues = createAsyncAction(
  'entityPortfolio/getEntityTransactionValue/request',
  'entityPortfolio/getEntityTransactionValue/success',
  'entityPortfolio/getEntityTransactionValue/failure',
)<
  GetEntityTransactionValueRequestPayload,
  GetEntityTransactionValueSuccessPayload,
  undefined
>();

export type GetUpcomingCashflowsRequestPayload = {
  company_entity_id: string;
  run_date: string;
  months_end: number;
  date_diff: boolean;
};

export const downloadUpcomingCashdlowsReport = createAsyncAction(
  'entityPortfolio/downloadUpcomingCashflowsReport/request',
  'entityPortfolio/downloadUpcomingCashflowsReport/success',
  'entityPortfolio/downloadUpcomingCashflowsReport/failure',
)<GetUpcomingCashflowsRequestPayload, undefined, undefined>();

export type GetEntityByIdRequestPayload = {
  entity_id: string;
};
export type GetEntityByIdSuccessPayload = {
  data: EntityData;
};
export const getEntityById = createAsyncAction(
  'entityPortfolio/getEntityById/request',
  'entityPortfolio/getEntityById/success',
  'entityPortfolio/getEntityById/failure',
)<GetEntityByIdRequestPayload, GetEntityByIdSuccessPayload, undefined>();

export type GetEntityDocumentsRequestPayload = {
  company_entity_id: string;
};
export type GetEntityDocumentsSuccessPayload = {
  data: Files;
};
export const getEntityFiles = createAsyncAction(
  'entityPortfolio/getEntityFiles/request',
  'entityPortfolio/getEntityFiles/success',
  'entityPortfolio/getEntityFiles/failure',
)<
  GetEntityDocumentsRequestPayload,
  GetEntityDocumentsSuccessPayload,
  undefined
>();

export type CounterPartryExposureRequestPayload = {
  currency_id: number;
  value_date?: string;
  visible_entities?: string[];
};

export type CounterPartyExposureSuccessPayload = {
  data: CounterpartyExposureData;
};

export const setCounterPartyExposure = createAsyncAction(
  'entityPortfolio/getCounterPartyExposure/request',
  'entityPortfolio/getCounterPartyExposure/success',
  'entityPortfolio/getCounterPartyExposure/failure',
)<
  CounterPartryExposureRequestPayload,
  CounterPartyExposureSuccessPayload,
  undefined
>();

export type GetDebtAndHedgeRequestPayload = {
  date: string;
  company_entity_list: string[];
  currency_id: number;
};

export type GetDebtAndHedgeSuccessPayload = {
  data: HedgedNotionalData[];
};

export const getDebtAndHedge = createAsyncAction(
  'entityPortfolio/getDebtAndHedge/request',
  'entityPortfolio/getDebtAndHedge/success',
  'entityPortfolio/getDebtAndHedge/failure',
)<GetDebtAndHedgeRequestPayload, GetDebtAndHedgeSuccessPayload, undefined>();

// debt reports
export type GetDebtValuationRequestPayload = {
  debt_id_list?: number[];
  run_date: string;
  report_start_date?: string;
  report_end_date?: string;
  historical_date_filter?: string;
};
export const getDebtValuationReport = createAsyncAction(
  'entityPortfolio/getDebtValuationReport/request',
  'entityPortfolio/getDebtValuationReport/success',
  'entityPortfolio/getDebtValuationReport/failure',
)<GetDebtValuationRequestPayload, undefined, undefined>();

export type GetDebtStatementRequestPayload = {
  debt_id_list: number[];
  valuation_date: string;
  curve_date?: string;
  report_currency_id: number;
  historical_date_filter?: string;
};

export const getDebtStatementReport = createAsyncAction(
  'entityPortfolio/getDebtStatementReport/request',
  'entityPortfolio/getDebtStatementReport/success',
  'entityPortfolio/getDebtStatementReport/failure',
)<GetDebtStatementRequestPayload, undefined, undefined>();

export type GetDebtUpcomingCashflowsRequestPayload = {
  run_date: string;
  company_entity_id?: string;
  months_end?: number | null;
  date_diff?: boolean;
};

export const getDebtUpcomingCashflowsReport = createAsyncAction(
  'entityPortfolio/getDebtUpcomingCashflowsReport/request',
  'entityPortfolio/getDebtUpcomingCashflowsReport/success',
  'entityPortfolio/getDebtUpcomingCashflowsReport/failure',
)<GetDebtUpcomingCashflowsRequestPayload, undefined, undefined>();

export type GetEntityCashflowsRequestPayload = {
  company_entity_id: string;
  user_currency_id: number;
  metric_date?: string | null;
};
export type GetEntityCashFlowsSuccessPayload = {
  data: EntityFlows[];
};

export const getEntityCashflows = createAsyncAction(
  'entityPortfolio/getEntityCashflows/request',
  'entityPortfolio/getEntityCashflows/success',
  'entityPortfolio/getEntityCashflows/failure',
)<
  GetEntityCashflowsRequestPayload,
  GetEntityCashFlowsSuccessPayload,
  undefined
>();

export type GetIFRSReportRequestPayload = {
  transaction_id_list: number[];
  valuation_date: string;
};

export const getIFRSReport = createAsyncAction(
  'entityPortfolio/getIFRSReport/request',
  'entityPortfolio/getIFRSReport/success',
  'entityPortfolio/getIFRSReport/failure',
)<GetIFRSReportRequestPayload, undefined, undefined>();

export type GetIFRSTradesRequestPayload = {
  company_entity_id: string;
  valuation_date: string;
};

export type GetIFRSTradesSuccessPayload = {
  data: number[];
};

export const getIFRSTrades = createAsyncAction(
  'entityPortfolio/getIFRSTrades/request',
  'entityPortfolio/getIFRSTrades/success',
  'entityPortfolio/getIFRSTrades/failure',
)<GetIFRSTradesRequestPayload, GetIFRSTradesSuccessPayload, undefined>();

export type GetCCSReportRequestPayload = {
  transaction_id_list: number[];
  valuation_date: string;
  currency_id: number;
};

export const getCCSReport = createAsyncAction(
  'entityPortfolio/getCCSReport/request',
  'entityPortfolio/getCCSReport/success',
  'entityPortfolio/getCCSReport/failure',
)<GetCCSReportRequestPayload, undefined, undefined>();

export type EntityS3RequestPayload = {
  file_key: string;
  file_name: string;
  user_id: string;
  trade_id?: number | null;
  comp_entity_id?: string | null;
  bk_entity_id?: string | null;
  document_type_id?: string | null;
  time_period_id?: number | null;
};

export const uploadDocument = createAsyncAction(
  'entityPortfolio/uploadDocument/request',
  'entityPortfolio/uploadDocument/success',
  'entityPortfolio/uploadDocument/failure',
)<EntityS3RequestPayload, undefined, undefined>();

export const setDate = createAction('entityPortfolio/setDate')<string>();

export type GetSponsorsByEntitiesRequestPayload = {
  company_entity_ids: string[];
};
export type GetSponsorsByEntitiesSuccessPayload = {
  data: SponsorsByEntity[];
};

export const getSponsorsByEntities = createAsyncAction(
  'entityPortfolio/getSponsorsByEntities/request',
  'entityPortfolio/getSponsorsByEntities/success',
  'entityPortfolio/getSponsorsByEntities/failure',
)<
  GetSponsorsByEntitiesRequestPayload,
  GetSponsorsByEntitiesSuccessPayload,
  undefined
>();

// scenario analysis graph api. same request as ScenarioAnalysisReport but with different endpoint
export type GetScenarioAnalysisDataSuccessPayload = {
  data: ScenarioAnalysisData;
};
export const getScenarioAnalysisData = createAsyncAction(
  'entityPortfolio/getScenarioAnalysisData/request',
  'entityPortfolio/getScenarioAnalysisData/success',
  'entityPortfolio/getScenarioAnalysisData/failure',
)<
  GetScenarioAnalysisDownloadSuccessPayload,
  GetScenarioAnalysisDataSuccessPayload,
  undefined
>();

export type GetDebtAccrualsRequestPayload = {
  debt_id_list: number[];
  run_date: string;
  report_start_date?: string;
  report_end_date?: string;
  historical_date_filter?: string;
};

export const getDebtAccrualsReport = createAsyncAction(
  'entityPortfolio/getDebtAccrualsReport/request',
  'entityPortfolio/getDebtAccrualsReport/success',
  'entityPortfolio/getDebtAccrualsReport/failure',
)<GetDebtAccrualsRequestPayload, undefined, undefined>();

export type GetPortfolioTradeValueRequestPayload = {
  company_entity_ids: string[];
  run_date: string;
  user_currency_id: number;
};

export type GetPortfolioTradeValuesSuccessPayload = {
  data: EntityTradeValuesData[];
};

export const getPortfolioTradeValues = createAsyncAction(
  'entityPortfolio/getPortfolioTradeValues/request',
  'entityPortfolio/getPortfolioTradeValues/success',
  'entityPortfolio/getPortfolioTradeValues/failure',
)<
  GetPortfolioTradeValueRequestPayload,
  GetPortfolioTradeValuesSuccessPayload,
  undefined
>();

export type GetMultiEntityValuationReportPayload = {
  company_entity_id_list: string[];
  run_date: string;
};

export const downloadMultiEntityValuationReport = createAsyncAction(
  'entityPortfolio/downloadMultiEntityValuationReport/request',
  'entityPortfolio/downloadMultiEntityValuationReport/success',
  'entityPortfolio/downloadMultiEntityValuationReport/failure',
)<GetMultiEntityValuationReportPayload, undefined, undefined>();

export type GetMultiEntityPortfolioStatementPayload = {
  company_entity_id_list: string[];
  run_date: string;
  include_fair_values?: boolean;
  include_bucket_cashflows?: boolean;
};

export const downloadMultiEntityPortfolioStatement = createAsyncAction(
  'entityPortfolio/downloadMultiEntityPortfolioStatement/request',
  'entityPortfolio/downloadMultiEntityPortfolioStatement/success',
  'entityPortfolio/downloadMultiEntityPortfolioStatement/failure',
)<GetMultiEntityPortfolioStatementPayload, undefined, undefined>();
